import { ConfigurationContext } from "Components/Generic/Configuration";
import { IKeyValue } from "Components/Generic/KeyValueGrid/IKeyValue";
import { Value } from "Components/Generic/KeyValueGrid/Value";
import { IMergeField } from "Model/Configuration/IMergeFields";
import { EntityType } from "Model/EntityType";
import { useCallback, useContext, useMemo } from "react";
import { isTrue } from "Utils/Boolean";
import { Configuration } from "Utils/Configuration";
import { isFunction } from "Utils/Function";
import { getItemById, getItemType } from "Utils/Item";
import { AV_ORIGIN_COLUMN_LIST, AV_RESULT_COLUMN_LIST } from "../../AddressValidation/AddressValidationColumns";
import { DialogType } from "../../Dialog/DialogType";
import { IChoiceProps } from "./IChoiceProps";
import { IUseChoice } from "./IUseChoice";

export function useChoice(props: IChoiceProps): IUseChoice {
  const {
    cache, onAction, onIsRelevant, result, selectedResultItemId, type
  } = props;
  const cachedProjectItem = useMemo(() => getItemById(cache, result?.[`@id`]), [cache, result]);

  const hasIsRelevantIcon = isTrue(result?.[`@isOrigin`]) && isFunction(onIsRelevant);
  const onIsRelevantClick = useCallback(() => {
    if (!isFunction(onIsRelevant)) {
      return;
    }
    onIsRelevant(result?.[`@id`]);
  }, [onIsRelevant, result]);

  const hasAction = isFunction(onAction);
  const onActionClick = useCallback(() => {
    if (!isFunction(onAction)) {
      return;
    }
    onAction(result);
  }, [onAction, result]);

  const isSelectedResult = result?.[`@resultId`] === selectedResultItemId;

  const config = useContext(ConfigurationContext);
  const entityType = getItemType(cachedProjectItem);
  const items = useMemo(
    () => getColumnsByType(type, isTrue(result?.[`@isOrigin`]), config, entityType).map<IKeyValue>((mergeField) => ({
      key: mergeField.fieldName,
      value: <Value fieldName={mergeField.fieldName} item={result} placeholder="-" />
    })),
    [config, entityType, result, type]
  );

  return {
    cachedProjectItem,
    items,
    hasAction,
    hasIsRelevantIcon,
    isSelectedResult,
    onActionClick,
    onIsRelevantClick
  };
}

function getColumnsByType(
  type: DialogType,
  isOrigin: boolean,
  config: Configuration,
  entityType: EntityType
): IMergeField[] {
  if (type === DialogType.DuplicateCheck) {
    return config.getDCMergeFields(entityType);
  }

  if (isOrigin) {
    return AV_ORIGIN_COLUMN_LIST.map((column) => ({ fieldName: column }));
  }

  return AV_RESULT_COLUMN_LIST.map((column) => ({ fieldName: column }));
}
